// Molalar — canlı "şu an molada" sayaçları + günlük mola kullanımı tablosu.

import { useCallback, useEffect, useRef, useState } from 'react';
import { closeBreak, getBreaks, grantBreakMinutes } from '../api';
import { GRANT_REASONS, REASON_META, tzOfBrands } from '../constants';
import { fmtDate, joinDot, pad2, todayInTz } from '../format';
import { useEscape, useExpanded, useNow, useRoster } from '../hooks';
import type { BrandId, BreaksData } from '../types';
import {
  EmptyState,
  Icon,
  Modal,
  Select,
  Skeleton,
  dateInputStyle,
  dotStyle,
  ghostBtnStyle,
  inputStyle,
  primaryBtnStyle,
} from '../ui';

export default function Breaks({ brands, isAdmin }: { brands: BrandId[]; isAdmin: boolean }) {
  // Varsayılan gün marka diliminde (BetBooker=Akra) — tarayıcı saati değil.
  const [mbDate, setMbDate] = useState(() => todayInTz(tzOfBrands(brands)));
  const [mbData, setMbData] = useState<BreaksData | null>(null);
  const [mbLoading, setMbLoading] = useState(false);
  const [mbErr, setMbErr] = useState<string | null>(null);
  // Force-close failures get their OWN strip: under the shared mbErr the supervisor read
  // "Mola verileri yüklenemedi — not_found" for an action that had nothing to do with loading.
  const [closeErr, setCloseErr] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const { toggle, isOpen } = useExpanded();

  const [modalOpen, setModalOpen] = useState(false);
  const [grantAgent, setGrantAgent] = useState('');
  const [grantMinutes, setGrantMinutes] = useState(15);
  const [grantReason, setGrantReason] = useState('Yoğunluk');
  const [granting, setGranting] = useState(false);

  const roster = useRoster();
  const now = useNow(1000);

  const brandsKey = brands.join(',');
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setMbLoading(true);
    setMbErr(null);
    // api.ts http() THROWS on 5xx/network. An uncaught throw here skipped setMbLoading(false),
    // so one blip left the page stuck on its skeleton until a manual reload — the busy flag
    // belongs in finally. A failed load keeps whatever data is already on screen and says so.
    try {
      const data = await getBreaks(mbDate, brands);
      if (seq.current !== my) return; // eski istek geç döndüyse yut
      setMbData(data);
    } catch (e) {
      if (seq.current !== my) return;
      setMbErr(e instanceof Error ? e.message : 'Mola verileri yüklenemedi');
    } finally {
      if (seq.current === my) setMbLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mbDate, brandsKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const allowed = roster.filter((a) => a.brands.some((b) => brands.includes(b)));

  const openGrant = () => {
    setGrantAgent(allowed[0] ? allowed[0].id : '');
    setGrantMinutes(15);
    setGrantReason('Yoğunluk');
    setModalOpen(true);
  };
  const closeGrant = () => setModalOpen(false);
  useEscape(modalOpen, closeGrant);

  const submitGrant = async () => {
    if (!grantAgent || granting) return;
    setGranting(true);
    try {
      await grantBreakMinutes(grantAgent, grantMinutes, grantReason);
      await load();
      setModalOpen(false);   // yalnızca başarıda kapan
    } catch {
      // Verilemedi (ör. sunucu reddi / ağ) — buton "…" da takılı kalmasın; modal açık kalır.
    } finally {
      setGranting(false);
    }
  };

  const closeBreakFor = async (agentId: string) => {
    if (closingId) return;
    setClosingId(agentId);
    setCloseErr(null);
    try {
      await closeBreak(agentId);
      await load();                 // kartın gerçekten kapandığını sunucudan doğrula
    } catch (e) {
      // Tek ifadede kur (§2): metin düğümü bölünürse EN çevirisi eşleşemez.
      setCloseErr(e instanceof Error && e.message ? 'Mola sonlandırılamadı — ' + e.message : 'Mola sonlandırılamadı');
    } finally {
      setClosingId(null);
    }
  };

  const showSkeleton = !mbData && mbLoading;
  const showEmpty = !!mbData && mbData.rows.length === 0;
  const showData = !!mbData && mbData.rows.length > 0;
  const emptyReason =
    brands.length === 0 ? 'Üstteki marka filtresinden en az bir marka seçin.' : 'Seçili markalarda temsilci bulunamadı.';
  const grantDisabled = granting || !grantAgent || !(grantMinutes > 0);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="calendar_month" size={18} color="var(--faint)" />
          <input
            type="date"
            value={mbDate}
            onChange={(e) => setMbDate(e.target.value)}
            style={dateInputStyle}
          />
        </div>
        {isAdmin && (
          <button
            onClick={openGrant}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 15px',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Icon name="more_time" size={18} />
            Mola Süresi Ekle
          </button>
        )}
      </div>

      {mbErr && (
        <div
          style={{
            marginBottom: '14px', padding: '10px 13px', borderRadius: '10px',
            background: 'rgba(239,93,93,.10)', border: '1px solid rgba(239,93,93,.35)',
            color: '#ef5d5d', fontSize: '13px',
          }}
        >
          {'Mola verileri yüklenemedi — ' + mbErr}
        </div>
      )}

      {closeErr && (
        <div
          style={{
            marginBottom: '14px', padding: '10px 13px', borderRadius: '10px',
            background: 'rgba(239,93,93,.10)', border: '1px solid rgba(239,93,93,.35)',
            color: '#ef5d5d', fontSize: '13px',
          }}
        >
          {closeErr}
        </div>
      )}

      {showSkeleton && <Skeleton />}

      {showEmpty && <EmptyState icon="free_breakfast" title="Kayıt yok" reason={emptyReason} />}

      {showData && mbData && (
        <>
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '14px',
              padding: '16px 18px',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <span style={{ ...dotStyle('#e0a23c'), animation: 'caaPulse 1.6s ease-in-out infinite' }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Şu An Molada</span>
              <span style={{ fontSize: '11.5px', color: 'var(--faint)' }}>canlı</span>
            </div>
            {mbData.onBreakNow.length === 0 && (
              <div
                style={{
                  padding: '18px',
                  textAlign: 'center',
                  color: 'var(--faint)',
                  fontSize: '13px',
                  background: 'var(--inset)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                }}
              >
                Şu anda molada olan temsilci yok.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(232px,1fr))', gap: '12px' }}>
              {mbData.onBreakNow.map((o) => {
                const sec = Math.max(0, Math.floor((now - o.startMs) / 1000));
                // hak = allowanceMin; günün ÖNCEKİ molaları (usedBeforeMin) + bu molanın geçen
                // süresi düşülür, eksi = aşım. Önceki molalar sayılmadığında kart, aynı satırdaki
                // tablo ve temsilcinin kendi paneli aşımı gösterirken yeşil "kalan" gösteriyordu.
                const remaining = o.allowanceMin - (o.usedBeforeMin ?? 0) - Math.floor(sec / 60);
                const over = remaining < 0;
                return (
                  <div
                    key={o.id}
                    style={{
                      background: 'var(--inset)',
                      border: '1px solid rgba(224,162,60,.28)',
                      borderRadius: '12px',
                      padding: '13px 14px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{o.name}</div>
                        {o.department && <div style={{ fontSize: '11px', color: 'var(--dim)' }}>{o.department}</div>}
                      </div>
                      <Icon name="local_cafe" size={19} color="#e0a23c" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '10px' }}>
                      <div>
                        <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.04em', marginBottom: '3px' }}>
                          GEÇEN SÜRE
                        </div>
                        <div
                          style={{
                            fontSize: '23px',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1,
                            color: over ? '#ef6d6d' : '#e0a23c',
                          }}
                        >
                          {Math.floor(sec / 60) + ':' + pad2(sec % 60)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: 'var(--dim)' }}>Başl. {o.startAt}</div>
                        <div style={{ fontSize: '11.5px', fontWeight: 600, color: over ? '#ef6d6d' : '#37c46b' }}>
                          {over ? 'Süre aşıldı' : remaining + ' dk hak kaldı'}
                        </div>
                      </div>
                    </div>
                    {/* Molayı sonlandır (PLAN req 5). Motorda hazırdı ama hiçbir yerden
                        çağrılmıyordu: süpervizör, hakkını saatlerce aşan bir molaya karşı
                        hiçbir işlem yapamıyordu. */}
                    {isAdmin && (
                      <button
                        onClick={() => void closeBreakFor(o.id)}
                        disabled={closingId === o.id}
                        style={{
                          marginTop: '11px', width: '100%', padding: '7px 10px',
                          background: 'transparent', border: '1px solid rgba(239,109,109,.4)',
                          borderRadius: '8px', color: '#ef6d6d', fontSize: '12px', fontWeight: 600,
                          cursor: closingId === o.id ? 'default' : 'pointer',
                          opacity: closingId === o.id ? 0.6 : 1,
                        }}
                      >
                        {closingId === o.id ? 'Kapatılıyor…' : 'Molayı Sonlandır'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '14px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '15px 18px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <Icon name="history" size={19} color="var(--accent)" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Günlük mola kullanımı</div>
                <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{fmtDate(mbDate)} · hak 60 dk + ek</div>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{mbData.rows.length}</span> temsilci
              </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: '720px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 96px 108px 92px 88px 78px 40px',
                    gap: '10px',
                    padding: '9px 18px',
                    borderBottom: '1px solid var(--border)',
                    fontSize: '10px',
                    fontWeight: 600,
                    letterSpacing: '.04em',
                    color: 'var(--faint)',
                  }}
                >
                  <span>TEMSİLCİ</span>
                  <span style={{ textAlign: 'right' }}>KULLANILAN</span>
                  <span style={{ textAlign: 'right' }}>HAK</span>
                  <span style={{ textAlign: 'right' }}>KALAN</span>
                  <span style={{ textAlign: 'right' }}>AŞIM</span>
                  <span style={{ textAlign: 'right' }}>OTURUM</span>
                  <span></span>
                </div>
                {mbData.rows.map((row) => {
                  const over = row.overageMin > 0; // aşım varsa kullanılan + aşım kırmızı
                  const expanded = isOpen(row.agentId);
                  return (
                    <div key={row.agentId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <div
                        onClick={() => toggle(row.agentId)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 96px 108px 92px 88px 78px 40px',
                          gap: '10px',
                          padding: '11px 18px',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{row.name}</div>
                          {row.department && <div style={{ fontSize: '11px', color: 'var(--dim)' }}>{row.department}</div>}
                        </div>
                        <span
                          style={{
                            textAlign: 'right',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '13.5px',
                            color: over ? '#ef6d6d' : 'var(--text)',
                          }}
                        >
                          {row.usedMin + ' dk'}
                        </span>
                        <span
                          style={{
                            textAlign: 'right',
                            fontSize: '12.5px',
                            color: 'var(--dim)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.allowanceMin + ' dk'}{' '}
                          <span style={{ color: 'var(--faint)', fontSize: '10px' }}>
                            {row.grantedExtra > 0 ? '60+' + row.grantedExtra : ''}
                          </span>
                        </span>
                        <span
                          style={{
                            textAlign: 'right',
                            fontSize: '12.5px',
                            color: 'var(--text)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.remainingMin + ' dk'}
                        </span>
                        <span
                          style={{
                            textAlign: 'right',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '13px',
                            color: over ? '#ef6d6d' : 'var(--faint)',
                          }}
                        >
                          {over ? '+' + row.overageMin + ' dk' : ''}
                        </span>
                        <span
                          style={{
                            textAlign: 'right',
                            fontSize: '12.5px',
                            color: 'var(--dim)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.sessionCount}
                        </span>
                        <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Icon name={expanded ? 'expand_more' : 'chevron_right'} size={20} color="var(--faint)" />
                        </span>
                      </div>
                      {expanded && (
                        <div style={{ padding: '4px 18px 14px', background: 'var(--row)' }}>
                          {row.sessions.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {row.sessions.map((se, i) => {
                                const rm = REASON_META[se.endReason];
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '14px',
                                      flexWrap: 'wrap',
                                      padding: '9px 0',
                                      borderBottom: '1px solid rgba(255,255,255,.04)',
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: '12.5px',
                                        color: 'var(--text)',
                                        fontVariantNumeric: 'tabular-nums',
                                        width: '120px',
                                        flex: 'none',
                                      }}
                                    >
                                      {se.startAt} – {se.endAt}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '12.5px',
                                        color: 'var(--dim)',
                                        width: '58px',
                                        flex: 'none',
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                    >
                                      {se.durationMin + ' dk'}
                                    </span>
                                    <span style={{ fontSize: '11px', color: 'var(--faint)', width: '78px', flex: 'none' }}>
                                      Bitiş:
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '10.5px',
                                        fontWeight: 600,
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        whiteSpace: 'nowrap',
                                        color: rm[0],
                                        background: rm[1],
                                        border: '1px solid ' + rm[2],
                                      }}
                                    >
                                      {se.endReason}
                                    </span>
                                    {se.idleFlag && (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          marginLeft: 'auto',
                                          fontSize: '11.5px',
                                          fontWeight: 600,
                                          color: '#f2784b',
                                        }}
                                      >
                                        <Icon name="warning" size={16} />
                                        {'Mola öncesi ' + se.idleBeforeMin + ' dk boşta'}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {row.sessions.length === 0 && (
                            <div style={{ padding: '8px 2px', fontSize: '12.5px', color: 'var(--faint)' }}>
                              Bu gün mola kaydı yok.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {modalOpen && (
        <Modal onClose={closeGrant}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginBottom: '18px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              <Icon name="more_time" size={20} color="var(--accent)" />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Mola Süresi Ekle</div>
              <div style={{ fontSize: '12px', color: 'var(--dim)' }}>Temsilciye ek mola hakkı tanımla</div>
            </div>
          </div>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--dim)',
              marginBottom: '7px',
              letterSpacing: '.04em',
            }}
          >
            TEMSİLCİ
          </label>
          <div style={{ marginBottom: '15px' }}>
            <Select
              value={grantAgent}
              onChange={setGrantAgent}
              autoFocus
              fullWidth
              options={allowed.map((a) => ({ value: a.id, label: joinDot(a.name, a.department) }))}
              style={{
                background: 'var(--inset)',
                borderRadius: '10px',
                padding: '11px 34px 11px 13px',
                fontSize: '13.5px',
                fontWeight: 400,
              }}
            />
          </div>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--dim)',
              marginBottom: '7px',
              letterSpacing: '.04em',
            }}
          >
            EK SÜRE (DAKİKA)
          </label>
          <input
            type="number"
            min="5"
            step="5"
            value={grantMinutes}
            onChange={(e) => setGrantMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
            style={{ ...inputStyle, marginBottom: '15px', fontVariantNumeric: 'tabular-nums' }}
          />
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--dim)',
              marginBottom: '7px',
              letterSpacing: '.04em',
            }}
          >
            SEBEP
          </label>
          <div style={{ marginBottom: '22px' }}>
            <Select
              value={grantReason}
              onChange={setGrantReason}
              fullWidth
              options={GRANT_REASONS.map((r) => ({ value: r, label: r }))}
              style={{
                background: 'var(--inset)',
                borderRadius: '10px',
                padding: '11px 34px 11px 13px',
                fontSize: '13.5px',
                fontWeight: 400,
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={closeGrant} style={{ ...ghostBtnStyle, flex: 1 }}>
              İptal
            </button>
            <button onClick={submitGrant} disabled={grantDisabled} style={{ ...primaryBtnStyle, flex: 2 }}>
              {granting ? 'Ekleniyor…' : 'Onayla ve ekle'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
